import { doc, getDoc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { Therapist } from "../types/therapist";
import type { ScheduleTherapist } from "../types/schedule";

const FIRESTORE_CACHE_DOC = doc(db, "_cache", "publicTherapists");

export type ScheduleTherapistListItem = ScheduleTherapist;

let inflight: Promise<Therapist[]> | null = null;

function normalizeTherapists(value: unknown): Therapist[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((t): t is Therapist => Boolean(t && typeof t === "object" && (t as Therapist).id))
    .sort((a, b) =>
      (a.nomePublico || "").localeCompare(b.nomePublico || "", "pt-BR", {
        sensitivity: "base",
      }),
    );
}

async function loadFromFirestore(): Promise<Therapist[] | null> {
  try {
    const snap = await getDoc(FIRESTORE_CACHE_DOC);
    if (!snap.exists()) return null;
    // Lista vazia é válida (ninguém na vitrine).
    return normalizeTherapists(snap.data()?.therapists);
  } catch {
    return null;
  }
}

async function loadFromCallable(): Promise<Therapist[] | null> {
  try {
    const fn = httpsCallable<void, { success: boolean; therapists: Therapist[] }>(
      functions,
      "getPublicTherapists",
    );
    const result = await fn();
    if (!result.data?.success) return null;
    return normalizeTherapists(result.data.therapists);
  } catch {
    return null;
  }
}

/** JSON estático no Hostinger — fallback quando Firestore/callable falham. */
async function loadFromStaticJson(): Promise<Therapist[] | null> {
  try {
    const res = await fetch("/data/public-therapists.json", { cache: "no-cache" });
    if (!res.ok) return null;
    const payload = (await res.json()) as { therapists?: unknown };
    return normalizeTherapists(payload.therapists);
  } catch {
    return null;
  }
}

/**
 * Carrega terapeutas da vitrine pública.
 * Fonte de verdade: Firestore `_cache/publicTherapists` (atualizado na hora
 * ao marcar/desmarcar no painel). Cloud Function como fallback.
 *
 * JSON estático só entra como fallback (ex.: regras Firestore ainda não deployadas).
 */
export async function fetchPublicTherapists(_options?: {
  /** @deprecated Ignorado — mantido só por compatibilidade de chamada. */
  onFast?: (therapists: Therapist[]) => void;
}): Promise<Therapist[]> {
  if (inflight) return inflight;

  inflight = (async () => {
    const firestoreList = await loadFromFirestore();
    if (firestoreList !== null) return firestoreList;

    const callableList = await loadFromCallable();
    if (callableList !== null) return callableList;

    const staticList = await loadFromStaticJson();
    return staticList ?? [];
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * Assina o cache da vitrine em tempo real.
 * Quando o admin marca/desmarca alguém, a página pública atualiza sozinha.
 * Se o snapshot falhar (ex.: regras), cai no callable getPublicTherapists.
 */
export function subscribePublicTherapists(
  onChange: (therapists: Therapist[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  let cancelled = false;

  const unsub = onSnapshot(
    FIRESTORE_CACHE_DOC,
    (snap) => {
      if (!snap.exists()) {
        onChange([]);
        return;
      }
      onChange(normalizeTherapists(snap.data()?.therapists));
    },
    (err) => {
      void (async () => {
        const callableList = await loadFromCallable();
        if (!cancelled && callableList !== null) {
          onChange(callableList);
          return;
        }

        const staticList = await loadFromStaticJson();
        if (!cancelled && staticList !== null) {
          onChange(staticList);
          return;
        }

        if (!cancelled) onError?.(err);
      })();
    },
  );

  return () => {
    cancelled = true;
    unsub();
  };
}

export function toScheduleTherapists(therapists: Therapist[]): ScheduleTherapist[] {
  return therapists.map((t) => ({
    id: t.id,
    nomePublico: t.nomePublico,
    title: t.title || "",
    sessionDuration: t.sessionDuration || 60,
    horariosDisponiveis: t.horariosDisponiveis || {},
  }));
}

/** Pré-carrega o cache Firestore (hover no menu). */
export function prefetchPublicTherapists(): void {
  void loadFromFirestore();
}
